import { useEffect, useMemo, useState } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import TableLoader from "../../../Common/ShimmerLoader/TableLoader";
import isGHMC from "../../../Common/helper/helpar";

const FIGMA = {
  navy: "var(--color-primary)",
  textMuted: "#6B7280",
  border: "#eef0f3",
};

// Same look as the shared tables (theme.css): light tinted header with
// primary-colour text, white rows separated by a thin divider, no grid lines.
const HEAD_BG =
  "linear-gradient(rgba(var(--color-primary-rgb), 0.07), rgba(var(--color-primary-rgb), 0.07)), #ffffff";

const PAGE_SIZE_OPTIONS = [5, 10, 25];

const MAIN_COLUMNS = [
  "S.No",
  "Route Code",
  "Route Description",
  "Ward",
  "Vehicle",
  "Route Type",
  "Stoppage",
  "Distance",
  "From Date",
  "To Date",
  "Actions",
];

const RouteMasterTable = ({
  rows = [],
  loading = false,
  searchTerm = "",
  onSearchChange,
  pagination,
  onPageChange,
  onPageSizeChange,
  onEdit,
}) => {
  const isProd = isGHMC();

  const thStyle = {
    position: "sticky",
    top: 0,
    zIndex: 10,
    whiteSpace: "nowrap",
    border: "none",
    background: HEAD_BG,
    color: "var(--color-primary)",
    padding: "12px 14px",
    fontSize: "12.5px",
    fontWeight: 600,
    fontFamily: '"Poppins", sans-serif',
    textAlign: "center",
  };
  const [localPage, setLocalPage] = useState(1);
  const [localRowsPerPage, setLocalRowsPerPage] = useState(5);

  const tdStyle = {
    whiteSpace: "nowrap",
    border: "none",
    borderBottom: `1px solid ${FIGMA.border}`,
    padding: "10px 14px",
    textAlign: "center",
    color: "#4b5565",
    fontSize: "13px",
    backgroundColor: "#FFFFFF",
  };

  const isServerPagination = Boolean(pagination);
  const currentPage = isServerPagination ? pagination.currentPage : localPage;
  const rowsPerPage = isServerPagination ? pagination.pageSize : localRowsPerPage;
  const totalResults = isServerPagination ? pagination.totalCount : rows.length;
  const totalPages = Math.max(1, Math.ceil(totalResults / rowsPerPage));

  const paginatedRows = useMemo(() => {
    if (isServerPagination) {
      const start = (currentPage - 1) * rowsPerPage;
      return rows.slice(start, start + rowsPerPage);
    }

    const start = (currentPage - 1) * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [rows, currentPage, rowsPerPage, isServerPagination]);

  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxVisible = 6;
    for (let i = 1; i <= Math.min(totalPages, maxVisible); i += 1) {
      pages.push(i);
    }
    return pages;
  }, [totalPages]);

  const rangeStart = totalResults ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const rangeEnd = Math.min(currentPage * rowsPerPage, totalResults);

  useEffect(() => {
    if (!isServerPagination) {
      setLocalPage(1);
    }
  }, [rows, isServerPagination]);

  useEffect(() => {
    if (!isServerPagination && localPage > totalPages) {
      setLocalPage(totalPages);
    }
  }, [localPage, totalPages, isServerPagination]);

  const handlePageChange = (page) => {
    if (isServerPagination) {
      onPageChange?.(page);
      return;
    }
    setLocalPage(page);
  };

  const handlePageSizeChange = (nextPageSize) => {
    if (isServerPagination) {
      onPageSizeChange?.(nextPageSize);
      return;
    }
    setLocalRowsPerPage(nextPageSize);
    setLocalPage(1);
  };

  const getRowId = (row) => row.routeId ?? row.routeCode;

  const renderRowCells = (row, serialNo) => (
    <>
      <td style={tdStyle}>{serialNo}</td>
      <td style={tdStyle}>{row.routeCode || "-"}</td>
      <td style={tdStyle}>{row.routeDescription || "-"}</td>
      <td style={tdStyle}>{row.ward || "-"}</td>
      <td style={tdStyle}>{row.vehicle || "-"}</td>
      <td style={tdStyle}>{row.routeTypeName || "-"}</td>
      <td style={tdStyle}>{row.stoppage ?? "-"}</td>
      <td style={tdStyle}>{row.distance || "-"}</td>
      <td style={tdStyle}>{row.fromDate || "-"}</td>
      <td style={tdStyle}>{row.toDate || "-"}</td>
      <td style={tdStyle}>
        <button
          type="button"
          className={isProd ? "edit-ghmc" : "edit-swm"}
          onClick={() => onEdit?.(row)}
          aria-label={`Edit ${row.routeCode}`}
        >
          <FontAwesomeIcon icon={faEdit} />
        </button>
      </td>
    </>
  );

  if (loading) {
    return <TableLoader height={500} columns={8} />;
  }

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            maxWidth: 420,
          }}
        >
         
          <input
            type="text"
            value={searchTerm}
            onChange={(event) => onSearchChange?.(event.target.value)}
            className="form-control bg-white"
            placeholder="Search Route Code / Description"
            style={{ width: "100%", fontSize: "14px" }}
          />
        </div>

        <h6
          style={{
            margin: "14px 0 0",
            fontSize: "16px",
            fontWeight: 600,
            color: "#231F20",
          }}
        >
          Route List
        </h6>
      </div>

      <div
        style={{
          overflow: "hidden",
          borderRadius: 16,
          border: `1px solid ${FIGMA.border}`,
          backgroundColor: "#FFFFFF",
          padding: "8px 10px 4px",
          boxShadow:
            "0 1px 2px rgba(17, 24, 39, 0.03), 0 6px 20px rgba(17, 24, 39, 0.05)",
        }}
      >
        <div style={{ maxHeight: 430, overflow: "auto" }}>
          <table
            style={{
              minWidth: 1200,
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              textAlign: "center",
              fontSize: "14px",
            }}
          >
            <thead>
              <tr>
                {MAIN_COLUMNS.map((col) => (
                  <th key={col} style={thStyle}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!paginatedRows.length ? (
                <tr>
                  <td
                    colSpan={MAIN_COLUMNS.length}
                    style={{ ...tdStyle, padding: "24px" }}
                  >
                    No data available
                  </td>
                </tr>
              ) : (
                paginatedRows.map((row, rowIndex) => {
                  const serialNo = (currentPage - 1) * rowsPerPage + rowIndex + 1;
                  return (
                    <tr key={getRowId(row)}>
                      {renderRowCells(row, serialNo)}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div
        style={{
          marginTop: 16,
          paddingTop: 12,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <p style={{ margin: 0, fontSize: "14px", color: FIGMA.textMuted }}>
          Showing {rangeStart}-{rangeEnd} of {totalResults} results
        </p>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 20,
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: "14px",
              color: FIGMA.textMuted,
              margin: 0,
            }}
          >
            Row per page
            <select
              value={rowsPerPage}
              onChange={(event) => handlePageSizeChange(Number(event.target.value))}
              style={{
                appearance: "none",
                borderRadius: 6,
                border: `1px solid ${FIGMA.border}`,
                backgroundColor: "#F0F4F8",
                padding: "6px 28px 6px 12px",
                fontSize: "14px",
                color: "#374151",
                outline: "none",
              }}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>

          <nav
            style={{ display: "flex", alignItems: "center", gap: 6 }}
            aria-label="Pagination"
          >
            <button
              type="button"
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                border: "none",
                background: "transparent",
                color: "#9CA3AF",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                opacity: currentPage === 1 ? 0.4 : 1,
              }}
              aria-label="Previous page"
            >
              <FiChevronLeft size={16} />
            </button>

            {pageNumbers.map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => handlePageChange(page)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 32,
                  height: 32,
                  borderRadius: 6,
                  padding: "0 10px",
                  border: "none",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                  backgroundColor:
                    currentPage === page ? FIGMA.navy : "#F0F4F8",
                  color: currentPage === page ? "#FFFFFF" : "#374151",
                }}
                aria-current={currentPage === page ? "page" : undefined}
              >
                {page}
              </button>
            ))}

            <button
              type="button"
              onClick={() =>
                handlePageChange(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                border: "none",
                background: "transparent",
                color: "#9CA3AF",
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                opacity: currentPage === totalPages ? 0.4 : 1,
              }}
              aria-label="Next page"
            >
              <FiChevronRight size={16} />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default RouteMasterTable;
